import type { AutomergeUrl } from "@automerge/automerge-repo";
import { RepoContext } from "@automerge/react";
import { useQueryClient } from "@tanstack/react-query";
import debug from "debug";
import { isDirInode } from "playground/fs/paths";
import { createPrivateBlobStore } from "playground/hal/blobStore/private";
import type { HostPlayground } from "playground/hal/index";
import { createPlayground } from "playground/hal/playground";
import { checkpointProject } from "playground/vcs";
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { invariant } from "ts-invariant";

import {
  modelSelectionForRun,
  type ModelExport,
} from "@/bear-sandbox/model-export";
import type { ModelDiagnostic } from "@/bear-sandbox/types";
import BearSandbox from "@/components/BearSandbox";
import { jigParseOptions } from "@/jigs/install";
import {
  subscribeExecution,
  type ExecutionOutcome,
} from "@/lib/bear-sandbox-bridge";
import type { Project } from "@/types/projects";

type BearSandboxSourceState = {
  hasBuilt: boolean;
  error: Error | null;
  isBuilding: boolean;
};

export type RunResult = {
  checkpointed: { file: string; heads: string[] }[];
  errors: string[];
  /** What the engine found wrong with the model it built; empty on a failed build. */
  diagnostics: readonly ModelDiagnostic[];
};

/**
 * A run a newer one replaced reports that, not a clean build: its caller
 * never saw this model, and an empty result would read as success.
 */
const SUPERSEDED: RunResult = {
  checkpointed: [],
  errors: ["Superseded by a newer run before this one finished; run again."],
  diagnostics: [],
};

type BearSandboxContextValue = {
  attachContainer: (element: HTMLDivElement | null) => void;
  sourceState: BearSandboxSourceState;
  run: (opts?: ModelExport & { remote?: boolean }) => Promise<RunResult>;
  getModelSelection: (entryPoint?: string) => ModelExport;
  /**
   * Always non-null inside `BearSandboxProvider`'s children — the provider
   * blocks rendering until the playground finishes constructing. Callers
   * can use `useBearSandbox().playground` without guarding.
   */
  playground: HostPlayground;
  setWorkspacePresenter: (
    handler: ((paths: string[]) => Promise<void>) | null,
  ) => void;
};

const runLog = debug("bear:run");

const BearSandboxContext = createContext<BearSandboxContextValue | null>(null);

type BearSandboxProviderProps = PropsWithChildren<{
  project: Project;
  projectId: string;
  indexUrl: AutomergeUrl;
  taskId?: string;
}>;

const BearSandboxProvider = ({
  children,
  project,
  projectId,
  indexUrl,
  taskId,
}: BearSandboxProviderProps) => {
  const [sourceState, setSourceState] = useState<BearSandboxSourceState>({
    hasBuilt: false,
    error: null,
    isBuilding: false,
  });

  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [fallbackContainer, setFallbackContainer] =
    useState<HTMLDivElement | null>(null);

  /** Build trigger — just the entry point; files are loaded lazily by sandbox */
  const [buildPayload, setBuildPayload] = useState<ModelExport | null>(null);
  const modelSelections = useRef(new Map<string, ModelExport>());
  const activeModelSelection = useRef<{
    indexUrl: AutomergeUrl;
    selection: ModelExport;
  }>({
    indexUrl,
    selection: {},
  });
  const getModelSelection = useCallback(
    (entryPoint?: string) =>
      modelSelections.current.get(`${indexUrl}:${entryPoint ?? ""}`) ?? {
        entryPoint,
      },
    [indexUrl],
  );

  const runIdRef = useRef(0);
  const repo = useContext(RepoContext);
  const queryClient = useQueryClient();
  const projectRef = useRef(project);
  projectRef.current = project;
  const workspacePresenter = useRef<
    ((paths: string[]) => Promise<void>) | null
  >(null);
  const setWorkspacePresenter = useCallback(
    (handler: ((paths: string[]) => Promise<void>) | null) => {
      workspacePresenter.current = handler;
    },
    [],
  );
  const presentWorkspace = useCallback(async (paths: string[]) => {
    invariant(
      workspacePresenter.current,
      "Task workspace presentation is unavailable",
    );
    await workspacePresenter.current(paths);
  }, []);

  const [playground, setPlayground] = useState<HostPlayground | null>(null);
  useEffect(() => {
    if (!repo) return;
    let cancelled = false;
    // Set before anything can call `upload()`, which is a method of it.
    createPlayground({
      repo,
      root: indexUrl,
      apps: jigParseOptions,
      blobs: createPrivateBlobStore({
        presignUrl: taskId
          ? `/api/tasks/${taskId}/uploads`
          : `/api/projects/${projectId}/uploads`,
      }),
      onAfterUpload: () => {
        // The browser HAL used to invalidate this cache internally; now the
        // app owns its own query key and does it here.
        queryClient.invalidateQueries({
          queryKey: ["project-files", projectId],
        });
      },
    }).then((pg) => {
      if (!cancelled) setPlayground(pg);
    });
    return () => {
      cancelled = true;
    };
  }, [repo, queryClient, projectId, indexUrl, taskId]);

  const run = useCallback(
    async (opts?: ModelExport & { remote?: boolean }): Promise<RunResult> => {
      const selection = modelSelectionForRun(
        activeModelSelection.current.indexUrl === indexUrl
          ? activeModelSelection.current.selection
          : {},
        opts?.entryPoint !== undefined
          ? modelSelections.current.get(`${indexUrl}:${opts.entryPoint}`)
          : undefined,
        opts,
      );
      activeModelSelection.current = { indexUrl, selection };
      modelSelections.current.set(
        `${indexUrl}:${selection.entryPoint ?? ""}`,
        selection,
      );
      const t0 = performance.now();
      const mode = opts?.remote ? "remote" : "local";
      runLog("run started (mode=%s)", mode);

      const currentRunId = ++runIdRef.current;
      const isStale = () => runIdRef.current !== currentRunId;

      setSourceState({
        hasBuilt: false,
        error: null,
        isBuilding: true,
      });

      const errors: string[] = [];
      let checkpointed: { file: string; heads: string[] }[] = [];

      try {
        const proj = projectRef.current;

        if (opts?.remote) {
          // Remote run: checkpoint on server, wait for sync
          const checkpointUrl = taskId
            ? `/api/tasks/${taskId}/checkpoint`
            : `/api/projects/${projectId}/checkpoint`;
          runLog("[checkpoint] fetching %s", checkpointUrl);
          const checkpointStart = performance.now();
          const res = await fetch(checkpointUrl, {
            method: "POST",
          });
          runLog(
            "[checkpoint] response %d %s (%.0fms)",
            res.status,
            res.statusText,
            performance.now() - checkpointStart,
          );
          if (!res.ok) {
            throw new Error(
              `Checkpoint failed: ${res.status} ${res.statusText}`,
            );
          }
          const body = (await res.json()) as {
            checkpointed: { file: string; heads: string[] }[];
            heads: Record<string, string[]>;
          };
          checkpointed = body.checkpointed;
          runLog(
            "[checkpoint] checkpointed %d files, %d heads to sync",
            checkpointed.length,
            Object.keys(body.heads).length,
          );

          if (isStale()) {
            runLog("[checkpoint] stale, aborting");
            return SUPERSEDED;
          }

          // Wait for server heads to arrive on local handles
          const SYNC_TIMEOUT_MS = 5000;
          const POLL_INTERVAL_MS = 50;
          invariant(playground, "playground is required to await sync");

          const syncStart = performance.now();
          runLog("[sync] waiting for %d files", Object.keys(body.heads).length);
          for (const [filename, expected] of Object.entries(body.heads)) {
            const inode = await playground.stat(filename);
            if (!inode || isDirInode(inode)) continue;
            if (!inode.uri.startsWith("automerge:")) continue;

            const fileStart = performance.now();
            runLog("[sync] repo.find(%s) for %s", inode.uri, filename);
            const handle = await repo!.find(inode.uri as AutomergeUrl);
            runLog(
              "[sync] repo.find resolved for %s (%.0fms), calling whenReady",
              filename,
              performance.now() - fileStart,
            );
            const whenReadyStart = performance.now();
            await handle.whenReady();
            runLog(
              "[sync] whenReady resolved for %s (%.0fms)",
              filename,
              performance.now() - whenReadyStart,
            );

            const expectedKey = [...expected].sort().join(",");
            const deadline = Date.now() + SYNC_TIMEOUT_MS;

            while (Date.now() < deadline) {
              if (isStale()) {
                runLog("[sync] stale, aborting");
                return SUPERSEDED;
              }
              const currentHeads = [...(handle.heads() ?? [])].sort().join(",");
              if (currentHeads === expectedKey) break;
              await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
            }

            const finalHeads = [...(handle.heads() ?? [])].sort().join(",");
            const matched = finalHeads === expectedKey;
            runLog(
              "[sync] %s %s (%.0fms)",
              filename,
              matched ? "synced" : "TIMED OUT",
              performance.now() - fileStart,
            );
          }
          runLog(
            "[sync] total sync phase: %.0fms",
            performance.now() - syncStart,
          );
        } else {
          // Local run: checkpoint locally
          const localStart = performance.now();
          runLog("[checkpoint] local checkpoint");
          if (repo) {
            const result = await checkpointProject(repo, proj.files, "run");
            checkpointed = result.checkpointed;
          }
          runLog(
            "[checkpoint] local checkpoint done (%.0fms)",
            performance.now() - localStart,
          );
        }

        if (isStale()) {
          runLog("[build] stale, aborting");
          return SUPERSEDED;
        }

        // Trigger sandbox build — both build and REPL load files lazily
        // via the playground IPC reader, so no eager collection here.
        runLog("[build] triggering sandbox build");
        setBuildPayload(selection);

        // Wait for execution completion (sandbox bundles + materializes)
        const execStart = performance.now();
        runLog("[exec] waiting for sandbox build + execution");
        const outcome = await new Promise<ExecutionOutcome>((resolve) => {
          const unsub = subscribeExecution((outcome) => {
            unsub();
            resolve(outcome);
          });
        });
        const executionError = "error" in outcome ? outcome.error : undefined;
        const diagnostics = "error" in outcome ? [] : outcome.diagnostics;
        runLog(
          "[exec] sandbox execution done (%.0fms)%s",
          performance.now() - execStart,
          executionError ? ` error: ${executionError.message}` : "",
        );

        if (isStale()) {
          runLog("[complete] stale, aborting");
          return SUPERSEDED;
        }
        if (executionError) {
          errors.push(executionError.message);
          setSourceState({
            hasBuilt: false,
            isBuilding: false,
            error: executionError,
          });
        } else {
          setSourceState({ hasBuilt: true, isBuilding: false, error: null });
        }
        runLog(
          "[complete] run finished (%.0fms total)",
          performance.now() - t0,
        );
        return { checkpointed, errors, diagnostics };
      } catch (err) {
        if (isStale()) {
          runLog(
            "[complete] stale after error (%.0fms total): %s",
            performance.now() - t0,
            err instanceof Error ? err.message : String(err),
          );
          return SUPERSEDED;
        }

        const errorObject =
          err instanceof Error
            ? err
            : new Error(typeof err === "string" ? err : "Unknown error");

        errors.push(errorObject.message);

        setSourceState({
          hasBuilt: false,
          isBuilding: false,
          error: errorObject,
        });

        runLog(
          "[complete] run failed (%.0fms total): %s",
          performance.now() - t0,
          errorObject.message,
        );
        return { checkpointed, errors, diagnostics: [] };
      }
    },
    [indexUrl, playground, projectId, repo, taskId],
  );

  const handleExecutionError = useCallback((error: Error) => {
    setSourceState((prev) => ({
      ...prev,
      error,
      isBuilding: false,
    }));
  }, []);

  const attachContainer = useCallback((element: HTMLDivElement | null) => {
    setContainer(element);
  }, []);

  const value = useMemo(
    () =>
      playground
        ? {
            attachContainer,
            sourceState,
            run,
            getModelSelection,
            playground,
            setWorkspacePresenter,
          }
        : null,
    [
      attachContainer,
      sourceState,
      run,
      getModelSelection,
      playground,
      setWorkspacePresenter,
    ],
  );

  const portalTarget = container ?? fallbackContainer;

  // Block rendering children until the playground is ready. This is the
  // contract that lets `useBearSandbox().playground` be non-null — no
  // child mounts before construction finishes, so consumers never see a
  // null. Loading is visually transparent (it's typically <100ms on cold
  // mount, faster on warm).
  if (!value || !playground) {
    return (
      <div
        ref={setFallbackContainer}
        className="pointer-events-none fixed left-[-9999px] top-0 h-0 w-0 overflow-hidden"
        aria-hidden
      />
    );
  }

  return (
    <BearSandboxContext.Provider value={value}>
      {children}
      <div
        ref={setFallbackContainer}
        className="pointer-events-none fixed left-[-9999px] top-0 h-0 w-0 overflow-hidden"
        aria-hidden
      />
      {portalTarget
        ? createPortal(
            <BearSandbox
              buildPayload={buildPayload ?? undefined}
              playground={playground}
              onExecutionError={handleExecutionError}
              onWorkspacePresent={presentWorkspace}
            />,
            portalTarget,
          )
        : null}
    </BearSandboxContext.Provider>
  );
};

export default BearSandboxProvider;

// eslint-disable-next-line react-refresh/only-export-components
export function useBearSandbox() {
  const context = useContext(BearSandboxContext);
  if (!context) {
    throw new Error("useBearSandbox must be used within a BearSandboxProvider");
  }
  return context;
}
